<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { VideoCardData } from '$lib/api/types';

  export let data: VideoCardData;
  export let editable = false;
  export let hovered = false;

  const dispatch = createEventDispatcher<{
    change: VideoCardData;
  }>();

  let editing = false;
  let editUrl = data.videoUrl;

  function startEditing(event: MouseEvent) {
    event.stopPropagation();
    editUrl = data.videoUrl;
    editing = true;
  }

  function handleSave() {
    if (editUrl !== data.videoUrl) {
      dispatch('change', { ...data, videoUrl: editUrl });
    }
    editing = false;
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      handleSave();
    }
    if (event.key === 'Escape') {
      editing = false;
    }
  }

  // Check if it's a YouTube URL
  $: youtubeId = extractYoutubeId(data.videoUrl);

  function extractYoutubeId(url: string): string | null {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?/]+)/);
    return match ? match[1] : null;
  }
</script>

<div class="h-full relative overflow-hidden">
  {#if editing}
    <div class="h-full p-4 flex flex-col gap-2 relative z-30">
      <input
        type="url"
        bind:value={editUrl}
        placeholder="動画URLを入力 (YouTube対応)"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
        on:blur={handleSave}
        on:keydown={handleKeydown}
      />
      <p class="text-xs text-gray-500">YouTubeのURLを入力してください</p>
    </div>
  {:else if youtubeId}
    <!-- YouTube Embed -->
    <iframe
      src="https://www.youtube.com/embed/{youtubeId}?autoplay={data.autoplay ? 1 : 0}&mute={data.mute ? 1 : 0}&loop={data.loop ? 1 : 0}"
      title="YouTube video"
      class="w-full h-full"
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowfullscreen
    />

    {#if editable && hovered}
      <button
        on:click={startEditing}
        class="absolute top-2 right-2 z-30 bg-black/50 text-white rounded px-2 py-1 text-xs hover:bg-black/70"
      >
        編集
      </button>
    {/if}
  {:else if data.videoUrl}
    <!-- Native Video -->
    <video
      src={data.videoUrl}
      poster={data.thumbnailUrl || undefined}
      class="w-full h-full object-cover"
      controls
      loop={data.loop}
      autoplay={data.autoplay}
      muted={data.mute}
    >
      <track kind="captions" />
    </video>

    {#if editable && hovered}
      <button
        on:click={startEditing}
        class="absolute top-2 right-2 z-30 bg-black/50 text-white rounded px-2 py-1 text-xs hover:bg-black/70"
      >
        編集
      </button>
    {/if}
  {:else}
    <div class="h-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
      <i class="lni lni-play text-3xl mb-2"></i>
      {#if editable}
        <button
          on:click={startEditing}
          class="text-sm text-blue-500 hover:underline relative z-30"
        >
          動画URLを設定
        </button>
      {:else}
        <span class="text-sm">動画なし</span>
      {/if}
    </div>
  {/if}
</div>
